import React, { useState } from 'react';
import {
  BattleState,
  BattleUnitState,
  TurnOrderEntry,
  HotbarSkill,
  BattleItem,
} from '../types/battle';
import { getHealthColor, isUnitStunned } from '../utils/battleUtils';
import { BattleSpeed } from '../utils/battleSpeed';
import BattleTurnOrderPanel from './BattleTurnOrderPanel';
import BattleGridViewport from './BattleGridViewport';
import BattleUnitInfoPanel from './BattleUnitInfoPanel';
import BattleActionsBar from './BattleActionsBar';
import BattleSidebarActions from './BattleSidebarActions';
import BattleLogPanel from './BattleLogPanel';

function getStatusIcon(effectType: string): string {
  switch (effectType) {
    case 'Poison': return '🧪';
    case 'Burn': return '🔥';
    case 'Invisibility': return '👻';
    case 'Root': return '🌿';
    case 'DamageBuff': return '⚔️';
    case 'DamageReduction': return '🛡️';
    case 'StatBuff': return '✨';
    case 'Stun': return '💫';
    default: return '📋';
  }
}

interface BattleMobileLayoutProps {
  battle: BattleState;
  currentTurnUnit: TurnOrderEntry | null;
  selectedUnit: BattleUnitState | null;
  displayUnit: BattleUnitState | null;
  isDisplayUnitCurrentTurn: boolean;
  turnTimeRemaining: number;

  attackMode: boolean;
  skillMode: boolean;
  movementMode: boolean;
  itemMode: boolean;
  selectedSkill: HotbarSkill | null;
  selectedItem: BattleItem | null;
  validTargets: BattleUnitState[];
  skillTargets: { x: number; y: number }[];
  actionError: string | null;
  battleItems: BattleItem[];
  equippedSkills: HotbarSkill[];

  onAttackUnit: (selectedUnitId: number, targetUnitId: number) => void;
  onExecuteSkill: (x: number, y: number) => void;
  onSelectUnit: (unit: BattleUnitState) => void;
  onEnterAttackMode: () => void;
  onEnterMovementMode: () => void;
  onSelectSkillNode: (skill: HotbarSkill) => void;
  onShowWeaponRange: (unit: BattleUnitState) => void;
  onClearWeaponRange: () => void;
  onShowSkillRange: (skill: HotbarSkill) => void;
  onClearSkillRange: () => void;
  onCancelMovement: () => void;
  onCancelAttack: () => void;
  onCancelSkill: () => void;
  onCancelItem: () => void;
  onSelectBattleItem: (item: BattleItem) => void;
  onEndTurn: () => void;
  onClearActionError: () => void;
  onInstantWin?: () => void;

  battleLog: string[];
  battleLogFilter: 'all' | 'combat' | 'movement' | 'important';
  onBattleLogFilterChange: (filter: 'all' | 'combat' | 'movement' | 'important') => void;
  logContentRef: React.RefObject<HTMLDivElement | null>;

  battleSpeed: BattleSpeed;
  onCycleBattleSpeed: () => void;
  onFleeBattle: () => void;
  showLegend: boolean;
  onToggleLegend: () => void;

  // Enemy threat-range overlay (GH-69)
  threatOverlayMode: 'off' | 'all' | number;
  onToggleThreatOverlayAll: () => void;
  onToggleThreatUnit: (unitId: number) => void;

  gridViewportProps: React.ComponentProps<typeof BattleGridViewport>;
}

const BattleMobileLayout: React.FC<BattleMobileLayoutProps> = ({
  battle, currentTurnUnit, selectedUnit, displayUnit, isDisplayUnitCurrentTurn, turnTimeRemaining,
  attackMode, skillMode, movementMode, itemMode, selectedSkill, selectedItem, validTargets, skillTargets,
  actionError, battleItems, equippedSkills,
  onAttackUnit, onExecuteSkill, onSelectUnit, onEnterAttackMode, onEnterMovementMode, onSelectSkillNode,
  onShowWeaponRange, onClearWeaponRange, onShowSkillRange, onClearSkillRange,
  onCancelMovement, onCancelAttack, onCancelSkill, onCancelItem, onSelectBattleItem, onEndTurn,
  onClearActionError, onInstantWin,
  battleLog, battleLogFilter, onBattleLogFilterChange, logContentRef,
  battleSpeed, onCycleBattleSpeed, onFleeBattle, showLegend, onToggleLegend,
  threatOverlayMode, onToggleThreatOverlayAll, onToggleThreatUnit,
  gridViewportProps,
}) => {
  const [sheetExpanded, setSheetExpanded] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const stats = displayUnit?.stats;

  return (
    <div className="battle-mobile">
      <div className="battle-mobile-topbar">
        <h4 className="battle-mobile-title">{battle.battleName}</h4>
        <div className="battle-mobile-topbar-actions">
          <button className="battle-mobile-icon-btn" onClick={() => setLogOpen(true)} title="Battle Log">📜</button>
          <button className="battle-mobile-icon-btn" onClick={() => setMenuOpen(o => !o)} title="More">⋯</button>
        </div>
        {menuOpen && (
          <div className="battle-mobile-overflow-menu">
            <button onClick={() => { setMenuOpen(false); onFleeBattle(); }}>Flee</button>
            <button onClick={() => { setMenuOpen(false); onCycleBattleSpeed(); }}>
              Speed: {battleSpeed === 'instant' ? 'Instant' : battleSpeed}
            </button>
            <button onClick={() => { setMenuOpen(false); onToggleLegend(); }}>
              {showLegend ? 'Hide Legend' : 'Legend'}
            </button>
            <button onClick={() => { setMenuOpen(false); onToggleThreatOverlayAll(); }}>
              {threatOverlayMode !== 'off' ? 'Hide Threat Range' : '🚨 Threat Range'}
            </button>
          </div>
        )}
      </div>

      <div className="battle-mobile-turnorder">
        <BattleTurnOrderPanel
          turnOrder={battle.turnOrder ?? []}
          units={battle.units ?? []}
          currentTurnUnit={currentTurnUnit}
          attackMode={attackMode}
          skillMode={skillMode}
          validTargets={validTargets}
          skillTargets={skillTargets}
          selectedUnit={selectedUnit}
          selectedSkill={selectedSkill}
          battleStatus={battle.status}
          turnTimeRemaining={turnTimeRemaining}
          showTimer={true}
          onAttackUnit={onAttackUnit}
          onExecuteSkill={onExecuteSkill}
          onSelectUnit={onSelectUnit}
          threatOverlayUnitId={typeof threatOverlayMode === 'number' ? threatOverlayMode : null}
          onToggleThreatUnit={onToggleThreatUnit}
        />
      </div>

      <div className="battle-mobile-grid">
        <BattleGridViewport {...gridViewportProps} />
      </div>

      <div className="battle-mobile-dock">
        {sheetExpanded && displayUnit && (
          <div className="battle-mobile-sheet-overlay">
            <div className="battle-mobile-sheet-header">
              <span>Unit Details</span>
              <button className="battle-mobile-icon-btn" onClick={() => setSheetExpanded(false)}>×</button>
            </div>
            <div className="battle-mobile-sheet-body">
              <BattleUnitInfoPanel displayUnit={displayUnit} isCurrentTurn={isDisplayUnitCurrentTurn} />
            </div>
          </div>
        )}

        {displayUnit && stats && (
          <button className="battle-mobile-peek" onClick={() => setSheetExpanded(e => !e)}>
            <span className="battle-mobile-peek-name">{displayUnit.name}{isDisplayUnitCurrentTurn ? ' •' : ''}</span>
            <span className="battle-mobile-peek-stat" style={{ color: getHealthColor(stats.currentHP / (stats.maxHP || 1)) }}>
              HP {stats.currentHP}/{stats.maxHP}
            </span>
            <span className="battle-mobile-peek-stat">AP {stats.currentAP}/{stats.maxAP}</span>
            <span className="battle-mobile-peek-stat">Move {stats.currentMovement}/{stats.maxMovement}</span>
            {isUnitStunned(displayUnit) && <span className="battle-mobile-peek-stat">💫</span>}
            {displayUnit.statusEffects?.map(effect => (
              <span key={effect.id} className="battle-mobile-peek-effect" title={effect.effectName}>
                {getStatusIcon(effect.effectType)}
              </span>
            ))}
            <span className="battle-mobile-peek-chevron">{sheetExpanded ? '▾' : '▴'}</span>
          </button>
        )}

        {selectedUnit?.isPlayerUnit && (
          <div className="battle-mobile-hotbar">
            <BattleActionsBar
              selectedUnit={selectedUnit}
              currentTurnUnit={currentTurnUnit}
              attackMode={attackMode}
              skillMode={skillMode}
              selectedSkill={selectedSkill}
              equippedSkills={equippedSkills}
              onEnterAttackMode={onEnterAttackMode}
              onSelectSkillNode={onSelectSkillNode}
              onShowWeaponRange={onShowWeaponRange}
              onClearWeaponRange={onClearWeaponRange}
              onShowSkillRange={onShowSkillRange}
              onClearSkillRange={onClearSkillRange}
            />
          </div>
        )}

        <div className="battle-mobile-primary-actions">
          <BattleSidebarActions
            movementMode={movementMode}
            attackMode={attackMode}
            skillMode={skillMode}
            itemMode={itemMode}
            selectedUnit={selectedUnit}
            currentTurnUnit={currentTurnUnit}
            battleStatus={battle.status}
            selectedSkill={selectedSkill}
            selectedItem={selectedItem}
            battleItems={battleItems}
            actionError={actionError}
            onCancelMovement={onCancelMovement}
            onCancelAttack={onCancelAttack}
            onCancelSkill={onCancelSkill}
            onCancelItem={onCancelItem}
            onEnterMovementMode={onEnterMovementMode}
            onEnterAttackMode={onEnterAttackMode}
            onSelectBattleItem={onSelectBattleItem}
            onEndTurn={onEndTurn}
            onClearActionError={onClearActionError}
            onInstantWin={onInstantWin}
          />
        </div>
      </div>

      {logOpen && (
        <div className="battle-mobile-log-overlay">
          <div className="battle-mobile-log-overlay-header">
            <span>Battle Log</span>
            <button className="battle-mobile-icon-btn" onClick={() => setLogOpen(false)}>×</button>
          </div>
          <BattleLogPanel
            entries={battleLog}
            filter={battleLogFilter}
            onFilterChange={onBattleLogFilterChange}
            logContentRef={logContentRef}
          />
        </div>
      )}
    </div>
  );
};

export default BattleMobileLayout;
